import { useCallback, useEffect, useRef, useState } from "react";
import { useDataProvider, useNotify } from "react-admin";
import { Views, type View } from "react-big-calendar";
import dayjs, { type Dayjs } from "dayjs";
import { formatIsoDate } from "@/utils/date-format";
import type { HouseVisitSlotCalendarEvent } from "./types";
import { mapHouseVisitSlotRowsToCalendarEvents } from "./map-house-visit-slots-calendar-events";

export function useHouseVisitSlotsCalendarEvents(view: View, date: Dayjs, eventId: string | null) {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const notifyRef = useRef(notify);
  notifyRef.current = notify;
  const fetchGenerationRef = useRef(0);
  const [events, setEvents] = useState<HouseVisitSlotCalendarEvent[]>([]);
  const dateKey = formatIsoDate(dayjs(date));

  const loadEvents = useCallback(async () => {
    if (!eventId) {
      setEvents([]);
      return;
    }

    const generation = ++fetchGenerationRef.current;
    const selectedDate = dayjs(dateKey);
    let rangeStart: Dayjs;
    let rangeEnd: Dayjs;

    if (view === Views.MONTH) {
      // Include leading/trailing days visible in the month grid.
      rangeStart = selectedDate.startOf("month").startOf("week");
      rangeEnd = selectedDate.endOf("month").endOf("week");
    } else if (view === Views.WEEK) {
      rangeStart = selectedDate.startOf("week");
      rangeEnd = selectedDate.endOf("week");
    } else {
      rangeStart = selectedDate.startOf("day");
      rangeEnd = selectedDate.endOf("day");
    }

    try {
      const { data } = await dataProvider.getList("houseVisitSlots", {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "date", order: "ASC" },
        filter: {
          eventId,
          start: formatIsoDate(rangeStart),
          end: formatIsoDate(rangeEnd),
        },
      });
      if (generation !== fetchGenerationRef.current) return;
      setEvents(mapHouseVisitSlotRowsToCalendarEvents(data as unknown[]));
    } catch {
      if (generation !== fetchGenerationRef.current) return;
      notifyRef.current("Error fetching house visit slots", { type: "warning" });
      setEvents([]);
    }
  }, [view, dateKey, dataProvider, eventId]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  return { events, reload: loadEvents };
}
